import {
  dispatchAction,
  ObservableState,
  persistentReducedStream,
  reducedStream,
  reducer,
  streamReducer,
} from '@ardoq/rxbeach';
import {
  ChangedSettings,
  PersistedWorkspaceModeSettings,
  WorkspaceModeInstanceViewSettings,
  WorkspaceModeViewKey,
  WorkspaceModeViewSettingsShape,
  WorkspaceModeViewSettingsWithChanges,
} from './types';
import { ViewSettingsContexts } from '@ardoq/api-types';
import { DEFAULT_WORKSPACE_MODE_SETTINGS } from './defaultStreamState';
import { cloneDeep, isEqual, pick } from 'lodash';
import { composeNewSettings, extractChangedSettings } from 'viewSettings/utils';
import {
  saveViewSettingsChange,
  workspaceModeViewSettingsUpdateRequested,
  WorkspaceModeViewSettingsUpdateRequestedPayload,
} from './actions';
import {
  isViewSettingsUpdateRelevantForInstance,
  mergeViewSettings,
  typedEntries,
} from './utils';
import { getSavedSettings$ } from './savedSettings$';
import { DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE } from './defaultPersistedSettings';
import {
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  tap,
} from 'rxjs';

/**
 * Set the saved settings value if the state property equals to the default settings, otherwise
 *  do not override the current settings, they presumably contain the last value.
 */
const applySavedSettings = (
  state: WorkspaceModeViewSettingsWithChanges,
  savedSettings: PersistedWorkspaceModeSettings
): WorkspaceModeViewSettingsWithChanges => {
  const currentSettings = state.currentSettings;

  const viewsToUpdate = typedEntries(currentSettings)
    .filter(([key, value]) =>
      isEqual(value, DEFAULT_WORKSPACE_MODE_SETTINGS[key])
    )
    .map(([key]) => key);

  // these are the views that have default view settings and should be updated with saved settings
  const relevantCurrentSettings: Partial<WorkspaceModeViewSettingsShape> = pick(
    cloneDeep(currentSettings),
    viewsToUpdate
  );

  // saved settings that correspond to the views that should be updated
  const relevantSavedSettings: Partial<PersistedWorkspaceModeSettings> = pick(
    cloneDeep(savedSettings),
    viewsToUpdate
  );

  // Pick only creates a shallow copy, _.mergeWith then mutates it.
  // Therefore we clone the arguments
  const mergedSettings: Partial<WorkspaceModeViewSettingsShape> =
    mergeViewSettings(relevantCurrentSettings, relevantSavedSettings);

  return {
    currentSettings: {
      ...state.currentSettings,
      ...mergedSettings,
    },
    changedSettings: [],
    persistent: false,
  };
};

const updateViewSettings = (
  state: WorkspaceModeViewSettingsWithChanges,
  {
    viewId,
    viewInstanceId,
    settings,
    persistent,
    settingsPath,
  }: WorkspaceModeViewSettingsUpdateRequestedPayload
): WorkspaceModeViewSettingsWithChanges => {
  const currentViewSettings = state.currentSettings[viewId];

  const nextSettings = composeNewSettings(
    currentViewSettings,
    settings,
    settingsPath
  );

  const changedSettings = extractChangedSettings({
    settingsPath,
    nextSettings,
    settings,
    // Typing partial object entries will result in a type assertion.
    // While this assertion should come from the function, the function
    // is currently shared with legacy code, so we place it here.
  }) as ChangedSettings<WorkspaceModeViewSettingsShape>;

  return {
    currentSettings: {
      ...state.currentSettings,
      [viewId]: nextSettings,
    },
    changedSettings,
    persistent,
    viewInstanceId,
    viewId,
  };
};

const savedSettings$ = getSavedSettings$(
  ViewSettingsContexts.WORKSPACE_MODE,
  DEFAULT_PERSISTED_SETTINGS_IN_WORKSPACE_MODE
);

const initialState: WorkspaceModeViewSettingsWithChanges = {
  currentSettings: cloneDeep(DEFAULT_WORKSPACE_MODE_SETTINGS),
  changedSettings: [],
  persistent: false,
};

/**
 * This is the 'global' view settings state for the respective environment. It holds
 * the initial state for all private streams created through new subscriptions.
 * (one for all views)
 */
const _workspaceModeSettingsState$: ObservableState<WorkspaceModeViewSettingsWithChanges> =
  persistentReducedStream<WorkspaceModeViewSettingsWithChanges>(
    `contextViewSettings-${ViewSettingsContexts.WORKSPACE_MODE}`,
    initialState,
    [
      reducer(workspaceModeViewSettingsUpdateRequested, updateViewSettings),
      streamReducer(savedSettings$, applySavedSettings),
    ]
  );

/**
 * This is the observable emitting updates to all subscribed private streams. It's main
 * purpose is to facilitate the persistence of changes to the backend.
 * (one stream for all views, but the persisted changes are for a specific view type)
 */
export const workspaceModeSettingsForAllViews$ =
  _workspaceModeSettingsState$.pipe(
    distinctUntilChanged((a, b) => isEqual(a, b)),
    tap(({ currentSettings, changedSettings, persistent, viewId }) => {
      if (!persistent || !viewId || !changedSettings.length) {
        return;
      }

      const modifiedViewSettings = currentSettings[viewId];

      dispatchAction(
        saveViewSettingsChange({
          contextId: ViewSettingsContexts.WORKSPACE_MODE,
          viewId,
          changedProperties: changedSettings,
          modifiedViewSettings: modifiedViewSettings,
        })
      );
    }),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

const getPrivateStreamUpdatesReducer =
  <T extends WorkspaceModeViewKey>(viewId: T) =>
  (
    state: WorkspaceModeInstanceViewSettings<T>,
    {
      currentSettings,
      changedSettings,
      persistent,
    }: WorkspaceModeViewSettingsWithChanges
  ): WorkspaceModeInstanceViewSettings<T> => ({
    ...state,
    currentSettings: currentSettings[viewId],
    // At this point, thanks to the filter, we know `settings.viewId` is `T`.
    // This means `currentSettings[subscriberViewId]` is correctly typed as `WorkspaceModeViewSettingsShape[T]`.
    // However, `changedSettings` is still `ChangedSettings<WorkspaceModeViewSettingsShape>`, which is a
    // broader type. To avoid runtime narrowing, we assert the more specific type for it to be accepted by the
    // downstream logic.
    changedSettings: changedSettings as [
      keyof WorkspaceModeViewSettingsShape[T],
      any,
    ][],
    persistent,
  });
/**
 * The streams below are 'private', they get the initial state from the global state stream,
 * but the updates are filtered to only include updates relevant for the specific view instance.
 * This is to avoid all view instances receiving all updates, which would lead to unnecessary
 * re-renders. It also facilitates different instances of the same view type having different
 * settings during their lifecycle.
 * (one stream per view instance)
 */
export const getWorkspaceModeViewSettingsWithChanges$ = <
  T extends WorkspaceModeViewKey,
>(
  subscriberViewId: T,
  subscriberViewInstanceId: string
): Observable<WorkspaceModeInstanceViewSettings<T>> => {
  const initialState$ = _workspaceModeSettingsState$.pipe(
    map(({ currentSettings }): WorkspaceModeInstanceViewSettings<T> => ({
      currentSettings: currentSettings[subscriberViewId],
      changedSettings: [],
      persistent: false,
      viewId: subscriberViewId,
      viewInstanceId: subscriberViewInstanceId,
    }))
  );

  const privateStreamUpdates$ = workspaceModeSettingsForAllViews$.pipe(
    filter(({ viewId: updatedViewId, viewInstanceId: updatedViewInstanceId }) =>
      isViewSettingsUpdateRelevantForInstance({
        subscriberViewId,
        subscriberViewInstanceId,
        updatedViewId,
        updatedViewInstanceId,
      })
    )
  );

  return reducedStream(
    `workspace-mode-settings-with-changes-${subscriberViewInstanceId}`,
    initialState$,
    [
      streamReducer(
        privateStreamUpdates$,
        getPrivateStreamUpdatesReducer(subscriberViewId)
      ),
    ]
  );
};

export const getWorkspaceModeViewSettings$ = <T extends WorkspaceModeViewKey>(
  subscriberViewId: T,
  subscriberViewInstanceId: string
) =>
  getWorkspaceModeViewSettingsWithChanges$(
    subscriberViewId,
    subscriberViewInstanceId
  ).pipe(map(({ currentSettings }) => currentSettings));
